import React, { useEffect, useRef, useState } from "react";
import {
  downloadTransaction,
  getTransactions,
  getTransactionsSandBox,
} from "../../services/transaction";
import toast from "react-hot-toast";
import TransactionModal from "./TransactionModal";
import Toggle from "../../components/Toggle";
import { useAuth } from "../../context/AuthContextProvider";
import LoadingScreen from "../../components/LoadingScreen";
import { getBusinessDetails } from "../../services/settings";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [status, setStatus] = useState(null);
  const [emailAddress] = useState(null);
  const [referenceId, setReferenceId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [transactModal, setTransactModal] = useState(false);
  const [transactInfo, setTransactInfo] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isFocused, setIsFocused] = useState(false);
  const [isEndDateFocused, setIsEndDateFocused] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [businessName, setBusinessName] = useState("");

  const { globalProfile } = useAuth();

  const filtersRef = useRef({
    startDate,
    endDate,
    orderId,
    status,
    emailAddress,
    referenceId,
  });

  // Keep filtersRef updated when values change
  useEffect(() => {
    filtersRef.current = {
      startDate,
      endDate,
      orderId,
      status,
      emailAddress,
      referenceId,
    };
  }, [startDate, endDate, orderId, status, emailAddress, referenceId]);

  // Custom debounce function
  const debounce = (func, delay) => {
    let timeoutId;
    return function (...args) {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };
  };

  const handleBusinessDetails = async () => {
    try {
      const res = await getBusinessDetails();
      setBusinessName(res.data.businessName);
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message);
      }
    }
  };

  useEffect(() => {
    handleBusinessDetails();
  }, [globalProfile]);

  const getTransactionsReqFun =
    globalProfile === "TEST" ? getTransactionsSandBox : getTransactions;

  useEffect(() => {
    const fetchTransactions = async () => {
      setIsPageLoading(true);
      try {
        const res = await getTransactionsReqFun(currentPage, pageSize, {
          startDate: filtersRef.current.startDate,
          endDate: filtersRef.current.endDate,
          orderId: filtersRef.current.orderId,
          status: filtersRef.current.status,
          emailAddress: filtersRef.current.emailAddress,
          referenceId: filtersRef.current.referenceId,
        });
        setTransactions(res.data?.result || []);
        setTotalTransactions(res.data?.count || 0);
      } catch (error) {
        if (error.response) {
          toast.error(error.response.data.message);
        }
      } finally {
        setIsPageLoading(false);
        setLoading(false);
      }
    };
    setLoading(true);
    fetchTransactions();
  }, [globalProfile, currentPage, pageSize, getTransactionsReqFun]);

  const handleSearchChange = debounce((e) => {
    setOrderId(e.target.value);
    if (e.target.value === "") {
      setOrderId(null);
    }
  }, 1000);

  const handleSearchReferenceId = debounce((e) => {
    setReferenceId(e.target.value);
    if (e.target.value === "") {
      setReferenceId(null);
    }
  }, 1000);

  const resetFilter = async () => {
    try {
      const res = await getTransactions(currentPage, pageSize, {
        startDate: null,
        endDate: null,
        orderId: null,
        status: null,
        emailAddress: null,
        referenceId: null,
      });
      setTransactions(res.data?.result || []);
      setFilterLoading(false);
      setTotalTransactions(res.data?.count || 0);
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message);
        setFilterLoading(false);
      }
    }
  };

  const handleProfile = (info) => {
    setTransactModal(true);
    setTransactInfo(info);
  };

  const handleDownload = async () => {
    try {
      const response = await downloadTransaction({
        startDate,
        endDate,
        orderId,
        status,
      });

      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Transaction_Report-${businessName}.xlsx`;

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message);
      }
    }
  };

  if (loading && !isPageLoading) {
    return <LoadingScreen title="Transactions" />;
  }

  const filter = async () => {
    setFilterLoading(true);
    try {
      const res = await getTransactions(currentPage, pageSize, {
        startDate,
        endDate,
        orderId,
        status,
        emailAddress,
        referenceId,
      });
      setTransactions(res.data?.result || []);
      setFilterLoading(false);
      setTotalTransactions(res.data?.count || 0);
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message);
        setFilterLoading(false);
      }
    }
  };

  const descriptionBodyTemplate = (rowData) => (
    <div className="py-2 px-1 break-words whitespace-normal max-w-[280px]">
      {rowData.description}
    </div>
  );

  const amountBodyTemplate = (rowData) => (
    <span>
      {typeof rowData.amount === "number"
        ? rowData.amount.toFixed(2)
        : rowData.amount || "-"}
    </span>
  );

  const dateBodyTemplate = (rowData) => (
    <span>
      {rowData.initiated
        ? new Date(rowData.initiated).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          })
        : "-"}
    </span>
  );

  const statusBodyTemplate = (rowData) => {
    const statusVal = rowData.status || "";
    const lower = statusVal.toLowerCase();
    const statusClass =
      lower === "approved"
        ? "text-green-500 font-semibold"
        : lower === "declined"
        ? "text-red-500 font-semibold"
        : lower === "initiated"
        ? "text-blue-500 font-semibold"
        : "text-gray-600 font-semibold";

    return (
      <span className={statusClass}>
        {statusVal
          ? statusVal.charAt(0).toUpperCase() + statusVal.slice(1).toLowerCase()
          : "-"}
      </span>
    );
  };

  const schemeBodyTemplate = (rowData) => (
    <div className="text-black-shade text-left font-normal w-[100.97px] h-[30px] rounded-lg py-1 px-1">
      {rowData.scheme || "-"}
    </div>
  );

  return (
    <div className="">
      <div className="flex flex-col xl:flex-row items-start justify-between">
        <h1 className="text-navy-blue text-2xl font-bold">Transactions</h1>

        <div className="flex sm:flex-row flex-col justify-center items-center">
          <Toggle />
        </div>
      </div>
      <div className="xl:flex items-center justify-between mt-10">
        <div className="xl:flex items-center gap-3">
          <div className="relative mt-5 sm:m-0">
            <input
              type="text"
              placeholder="Search OrderID"
              onChange={handleSearchChange}
              className="xl:w-[250px] w-full h-[40px] rounded-[10px] px-2 border border-gray-300 focus:outline-none"
            />
            <span>
              <svg
                className="absolute right-8 top-3"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g opacity="0.5">
                  <path
                    d="M7.22235 13.4445C10.6589 13.4445 13.4447 10.6587 13.4447 7.22223C13.4447 3.78579 10.6589 1 7.22235 1C3.78584 1 1 3.78579 1 7.22223C1 10.6587 3.78584 13.4445 7.22235 13.4445Z"
                    stroke="#030229"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M15.0001 15.0005L11.6167 11.6172"
                    stroke="#030229"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
              </svg>
            </span>
          </div>

          <div className="relative  mt-2 xl:mt-0">
            <input
              type="text"
              placeholder="Start date"
              id="start-date"
              className="focus:outline-none mt-2 xl:mt-0 w-full xl:w-[130px] h-[40px] px-3 rounded-[5px] border border-gray-300"
              onChange={(e) => setStartDate(e.target.value)}
              onFocus={(e) => {
                e.target.type = "date";
                setIsFocused(true);
              }}
              onBlur={(e) => {
                e.target.type = "text";
                setIsFocused(false);
              }}
            />
            {!isFocused && (
              <span>
                <svg
                  className="absolute right-4 top-[25px] xl:top-[18px]"
                  width="9"
                  height="6"
                  viewBox="0 0 9 6"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    opacity="0.5"
                    d="M0.5 1L4.5 5L8.5 1"
                    stroke="black"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            )}
          </div>

          <div className="relative  mt-2 xl:mt-0">
            <input
              type="text"
              placeholder="End date"
              id="end-date"
              className="focus:outline-none w-full mt-2 xl:mt-0 xl:w-[130px] h-[40px] px-3 rounded-[5px] border border-gray-300"
              onChange={(e) => setEndDate(e.target.value)}
              onFocus={(e) => {
                e.target.type = "date";
                setIsEndDateFocused(true);
              }}
              onBlur={(e) => {
                e.target.type = "text";
                setIsEndDateFocused(false);
              }}
            />
            {!isEndDateFocused && (
              <span>
                <svg
                  className="absolute right-4 top-[25px] xl:top-[18px]"
                  width="9"
                  height="6"
                  viewBox="0 0 9 6"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    opacity="0.5"
                    d="M0.5 1L4.5 5L8.5 1"
                    stroke="black"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            )}
          </div>
          <div>
            <input
              type="text"
              placeholder="Search ReferenceId"
              onChange={handleSearchReferenceId}
              className="xl:w-[200px] mt-2 xl:mt-0  w-full h-[40px] rounded-[10px] px-2 border border-gray-300 focus:outline-none"
            />
          </div>
          <button
            className="text-white bg-primary mt-2 xl:mt-0 hover:bg-[rgba(var(--primary-rgb),0.9)] lg:mr-1 w-[130px] lg:w-[160px] h-[42px] rounded-[10px] mb-3 md:mb-0"
            onClick={filter}
          >
            Filter
          </button>
        </div>
        <div className="relative">
          {!loading && (
            <>
              <button
                onClick={handleDownload}
                className="text-white bg-primary mt-2 xl:mt-0 w-[130px] lg:w-[160px] h-[42px] rounded-[10px] mb-3 md:mb-0"
              >
                Export
              </button>
              <span>
                <svg
                  className="absolute left-6 lg:left-10 top-[25px] xl:top-[18px]"
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_4169_8547)">
                    <path
                      d="M6.91254 3.75H6.25004V1.66667C6.25004 1.4375 6.06254 1.25 5.83337 1.25H4.16671C3.93754 1.25 3.75004 1.4375 3.75004 1.66667V3.75H3.08754C2.71671 3.75 2.52921 4.2 2.79171 4.4625L4.70421 6.375C4.86671 6.5375 5.12921 6.5375 5.29171 6.375L7.20421 4.4625C7.46671 4.2 7.28337 3.75 6.91254 3.75ZM2.08337 7.91667C2.08337 8.14583 2.27087 8.33333 2.50004 8.33333H7.50004C7.72921 8.33333 7.91671 8.14583 7.91671 7.91667C7.91671 7.6875 7.72921 7.5 7.50004 7.5H2.50004C2.27087 7.5 2.08337 7.6875 2.08337 7.91667Z"
                      fill="white"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_4169_8547">
                      <rect width="10" height="10" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </span>
            </>
          )}
        </div>
      </div>

      <div className="bg-white mr-5 md:mr-0 sm:mt-4 sm:ml-3 sm:px-7 sm:py-5 rounded-lg shadow-sm">
        <div className="flex items-center sm:justify-between mb-4">
          <div className="flex flex-col-reverse sm:flex-row sm:items-center">
            <p className="border-r pr-4 text-sm font-bold text-black-shade">
              Transactions
            </p>

            <div className="flex sm:flex-row items-center border-r px-4">
              <div className="flex items-center gap-1 mr-2">
                <div className="rounded-full bg-green-400 w-[6px] h-[6px]"></div>
                <p
                  className="text-[12px] font-normal text-black-shade cursor-pointer"
                  onClick={() => setStatus("APPROVED")}
                >
                  Approved
                </p>
              </div>
              <div className="flex items-center gap-1 mr-2">
                <div className="rounded-full bg-red-400 w-[6px] h-[6px]"></div>
                <p
                  className="text-[12px] font-normal text-black-shade cursor-pointer"
                  onClick={() => setStatus("Declined")}
                >
                  Declined
                </p>
              </div>
            </div>

            <div className="flex items-center border-r px-2 gap-2">
              <div className="flex items-center gap-1">
                <button
                  className="text-black bg-white text-[12px] pl-3"
                  onClick={resetFilter}
                >
                  All
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="card overflow-x-auto">
          <DataTable
            value={transactions}
            loading={isPageLoading || filterLoading}
            paginator
            lazy
            first={(currentPage - 1) * pageSize}
            rows={pageSize}
            totalRecords={totalTransactions || 0}
            onPage={(e) => {
              setCurrentPage(e.page + 1);
              setPageSize(e.rows);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onRowClick={(e) => handleProfile(e.data)}
            rowClassName={() => "cursor-pointer"}
            emptyMessage={
              startDate || endDate
                ? "No transactions found"
                : "No transactions"
            }
            tableStyle={{ minWidth: "90rem" }}
            stripedRows
            responsiveLayout="scroll"
            className="p-datatable-sm"
          >
            <Column field="id" header="#" sortable />
            <Column
              field="description"
              header="Description"
              body={descriptionBodyTemplate}
              sortable
            />
            <Column
              field="amount"
              header="Amount"
              body={amountBodyTemplate}
              sortable
            />
            <Column field="currency" header="Currency" sortable />
            <Column
              field="initiated"
              header="Transaction Date"
              body={dateBodyTemplate}
              sortable
            />
            <Column
              field="status"
              header="Status"
              body={statusBodyTemplate}
              sortable
            />
            <Column field="pan" header="Masked PAN" sortable />
            <Column
              field="scheme"
              header="Scheme"
              body={schemeBodyTemplate}
              sortable
            />
            <Column field="customerName" header="Customer Name" sortable />
            <Column field="customerEmail" header="Customer Email" sortable />
            <Column field="referenceId" header="Reference ID" sortable />
          </DataTable>
        </div>
      </div>
      {transactModal && (
        <TransactionModal
          setIsModalOpen={setTransactModal}
          transactData={transactInfo}
        />
      )}
    </div>
  );
};

export default Transactions;
